<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SendingRotationFields from 'dashboard/components-next/Campaigns/Rotation/SendingRotationFields.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxIds: [],
  rotationSettings: { mode: 'round_robin', message_delay: 0, batch_size: 1, batch_delay: 0, inbox_reuse_delay: 0, jitter: 0 },
  scheduledAt: null,
  selectedAudience: [],
  audienceCsv: null,
  audienceLabel: '',
};

const state = reactive({ ...initialState });
const templateParserRefs = ref({});
const templateSelections = reactive({});
const csvColumns = ref([]);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxIds: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = inboxId => {
  const templates = formState.getFilteredWhatsAppTemplates.value(inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
};

const selectedTemplate = inboxId =>
  templateOptions(inboxId).find(option => option.value === templateSelections[inboxId])?.template;

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxIds', 'INBOX'),
}));

const hasRequiredTemplateParams = computed(() =>
  state.inboxIds.length > 0 && state.inboxIds.every(inboxId => {
    const parser = templateParserRefs.value[inboxId];
    return templateSelections[inboxId] && parser?.v$?.$invalid === false;
  })
);

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    (!state.audienceCsv && !state.selectedAudience.length)
);

const readCsvHeaders = event => {
  const [file] = event.target.files || [];
  state.audienceCsv = file || null;
  if (!file) return (csvColumns.value = []);
  const reader = new FileReader();
  reader.onload = () => {
    const firstLine = String(reader.result || '').split(/\r?\n/)[0] || '';
    csvColumns.value = firstLine
      .replace(/^\uFEFF/, '')
      .split(/[;,\t]/)
      .map(column => column.trim().replace(/^"|"$/g, ''))
      .filter(Boolean);
  };
  reader.readAsText(file.slice(0, 16 * 1024));
};

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, { ...initialState, inboxIds: [], rotationSettings: { ...initialState.rotationSettings } });
  Object.keys(templateSelections).forEach(key => delete templateSelections[key]);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const templateParamsForInbox = inboxId => {
  const currentTemplate = selectedTemplate(inboxId);
  const parserData = templateParserRefs.value[inboxId];
  return {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };
};

const prepareCampaignDetails = () => {
  const primaryInboxId = state.inboxIds[0];
  const primaryParser = templateParserRefs.value[primaryInboxId];
  return {
    title: state.title,
    message: primaryParser?.renderedTemplate || '',
    template_params: templateParamsForInbox(primaryInboxId),
    inbox_id: primaryInboxId,
    inboxes: state.inboxIds.map(inboxId => ({
      inbox_id: inboxId,
      template_params: templateParamsForInbox(inboxId),
    })),
    rotation_settings: { ...state.rotationSettings },
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({ id, type: 'Label' })),
    audienceCsv: state.audienceCsv,
    audienceLabel: state.audienceLabel || state.title,
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const setTemplateParserRef = (inboxId, element) => {
  if (element) templateParserRefs.value[inboxId] = element;
  else delete templateParserRefs.value[inboxId];
};

watch(
  () => [...state.inboxIds],
  inboxIds => {
    Object.keys(templateSelections).forEach(inboxId => {
      if (!inboxIds.includes(Number(inboxId))) delete templateSelections[inboxId];
    });
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        id="inbox"
        v-model="state.inboxIds"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div
      v-for="inboxId in state.inboxIds"
      :key="inboxId"
      class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
    >
      <label :for="`template-${inboxId}`" class="text-sm font-medium text-n-slate-12">
        {{ inboxOptions.find(option => option.value === inboxId)?.label }} —
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        :id="`template-${inboxId}`"
        v-model="templateSelections[inboxId]"
        :options="templateOptions(inboxId)"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
      />
      <WhatsAppTemplateParser
        v-if="selectedTemplate(inboxId)"
        :ref="element => setTemplateParserRef(inboxId, element)"
        :template="selectedTemplate(inboxId)"
        :csv-columns="csvColumns"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak">
      <label class="text-sm font-medium text-n-slate-12">Lista CSV para esta campanha</label>
      <input accept=".csv,text/csv" type="file" @change="readCsvHeaders" />
      <Input
        v-if="state.audienceCsv"
        v-model="state.audienceLabel"
        label="Etiqueta da lista"
        placeholder="Ex.: Clientes Julho"
      />
      <p class="text-xs text-n-slate-11">
        A lista será importada e etiquetada ao criar a campanha. Escolha as colunas nas variáveis do template.
      </p>
    </div>

    <SendingRotationFields v-model="state.rotationSettings" />

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      message="Opcional: se deixar vazio, a campanha ficará aguardando seu clique em Iniciar."
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
